import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';
import { ActivityLog } from '@/components/AdminLogs/AdminLogDetailsPopup';

export enum ActionCode {
  UPDATE = 'UPDATE',
  CREATE = 'CREATE',
  DELETE = 'DELETE',
}

export interface ConfigLogItem {
  id: string;
  userName: string;
  role: string;
  date: string;
  functionName: string;
  action: {
    name: string;
    code: ActionCode;
  };
}

export interface ConfigLogsResponse {
  payload: {
    content: ConfigLogItem[];
    totalElements: number;
  };
}

export interface GetConfigLogsParams {
  page?: number;
  limit?: number;
  [key: string]: unknown;
}

export interface ActivityLogUser {
  name: string;
  role: string;
}

export interface ActivityLogItem {
  action: { name: string; code: string };
  user: ActivityLogUser;
  message: string;
  date: string;
}

export type ActivityLogsResponse = { payload: ActivityLogItem[] };

export interface GetActivityLogsParams {
  type: string;
  id: string;
  [key: string]: unknown;
}

export interface AuditLogFunctionItem {
  name: string;
  code: string;
}

export interface AuditLogActionItem {
  name: string;
  code: string;
}

export interface AuditLogRoleItem {
  name: string;
  code: string;
}

export const configLogsService = createApi({
  reducerPath: 'configLogsService',
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getConfigLogs: builder.query<
      ConfigLogsResponse,
      GetConfigLogsParams | void
    >({
      query: (params) => ({
        url: '/aggregation-service/v1/operator/audit-logs',
        method: 'GET',
        params,
      }),
    }),
    getConfigLogById: builder.query<{ payload: ActivityLog }, string>({
      query: (id) => ({
        url: `/aggregation-service/v1/operator/audit-logs/${id}`,
        method: 'GET',
      }),
    }),
    getActivityLogs: builder.query<ActivityLogsResponse, GetActivityLogsParams>(
      {
        query: (params) => ({
          url: '/aggregation-service/v1/operator/activity-logs',
          method: 'GET',
          params,
        }),
      }
    ),
    getAuditLogFunctions: builder.query<AuditLogFunctionItem[], void>({
      query: () => ({
        url: '/aggregation-service/v1/operator/audit-logs/functions',
        method: 'GET',
      }),
      transformResponse: (
        response: AuditLogFunctionItem[] | { payload?: AuditLogFunctionItem[] }
      ) => {
        if (Array.isArray(response)) return response;
        if (Array.isArray(response?.payload)) return response.payload;
        return [];
      },
    }),
    getAuditLogActions: builder.query<AuditLogActionItem[], void>({
      query: () => ({
        url: '/aggregation-service/v1/operator/audit-logs/actions',
        method: 'GET',
      }),
      transformResponse: (
        response: AuditLogActionItem[] | { payload?: AuditLogActionItem[] }
      ) => {
        if (Array.isArray(response)) return response;
        if (Array.isArray(response?.payload)) return response.payload;
        return [];
      },
    }),
    getAuditLogRoles: builder.query<AuditLogRoleItem[], void>({
      query: () => ({
        url: '/aggregation-service/v1/operator/audit-logs/roles',
        method: 'GET',
      }),
      transformResponse: (
        response: AuditLogRoleItem[] | { payload?: AuditLogRoleItem[] }
      ) => {
        if (Array.isArray(response)) return response;
        if (Array.isArray(response?.payload)) return response.payload;
        return [];
      },
    }),
  }),
});

export const {
  useGetConfigLogsQuery,
  useGetConfigLogByIdQuery,
  useGetActivityLogsQuery,
  useGetAuditLogFunctionsQuery,
  useGetAuditLogActionsQuery,
  useGetAuditLogRolesQuery,
} = configLogsService;
